import React, { FC, useContext, useEffect, useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  useTheme,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material';
import moment from 'moment';
import Header from '../../components/Header';
import { FilterContext } from '../../contexts/FilterContext';
import {
  fetchAnfoValuation,
  AnfoValuationRow,
  fetchPlxWerbeaktionConfig,
  PlxWerbeaktionConfig,
} from '../../helpers/plxOrdersData';
import { tokens } from '../../theme';
import LoadingSpinner from '../../components/LoadingSpinner';
import { formatNumber } from '../../helpers/numberUtils';
import { TERMS } from '../../helpers/kpiDefinitions';
import InfoTip from '../../components/InfoTip';
import Explainer, { ExplainerSection } from '../../components/Explainer';
import TableWidget, { HeaderMappingItem } from '../../helpers/tableBuilder';

const WINDOW_LABEL: Record<string, string> = {
  current: 'aktuell',
  historisch: 'eigener letzter Zeitraum',
  pool: 'Durchschnitt',
  onetime: 'Einmalfaktura',
  zellen: 'je Zahlungsart (Testphase / Zahlung vorab)',
};

const EXPLAINER: ExplainerSection[] = [
  {
    label: 'Berechnung',
    body: (
      <>
        Der Anfo-Wert ist R × G: R = was eine Anfo des Produkts in den ersten 75 Tagen netto
        einbringt, G = Hochrechnung auf 36 Monate. Die Spalten „Modell: R“, „G“, „Grundlage“ und
        „n“ zeigen den wirksamen Monatslauf.
      </>
    ),
  },
  {
    label: 'Aussage',
    body: (
      <>
        „Ø bis Tag 75“ ist dieselbe Größe wie R, gemessen an den Anfos DIESES Filters, die älter als
        95 Tage sind. „Abweichung“ = Ø bis Tag 75 − R: positiv heißt, diese Anfos haben in den ersten
        75 Tagen mehr gebracht, als das Modell für das Produkt ansetzt. Der bisher bezahlte Umsatz
        (Umsatz/Anfo) ist dafür kein Maßstab — er wächst mit dem Alter der Anfos weiter.
      </>
    ),
  },
  {
    label: 'Wichtig',
    highlight: true,
    body: (
      <>
        Ohne reife Anfos im Filter (zu junges Zeitfenster) bleibt „Ø bis Tag 75“ leer. Vor dem ersten
        Modelllauf sind die Modellspalten leer.
      </>
    ),
  },
];

/**
 * `eingebettet`: ohne eigenen Seitenkopf und Außenrand — so zeigt die Sektion die Seite als Unterseite
 * /auswertungen/umsatz/anfo-bewertung (P5, 27.10.2026). Ohne den Schalter unverändert.
 */
const AnfoValuationPage: FC<{ eingebettet?: boolean }> = ({ eingebettet = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { filterState } = useContext(FilterContext)!;
  const [data, setData] = useState<AnfoValuationRow[]>([]);
  const [summary, setSummary] = useState<AnfoValuationRow | null>(null);
  const [loading, setLoading] = useState(true);
  // Ein Ladefehler muss als Fehler erscheinen, nicht als „0 Anfos · 0,00 €“ (Bedienprüfung 28.10.).
  const [fehler, setFehler] = useState<string | null>(null);
  const [excludeTestOrders, setExcludeTestOrders] = useState(true);
  const [selectedWkz, setSelectedWkz] = useState<string[]>([]);
  const [wkzOptions, setWkzOptions] = useState<PlxWerbeaktionConfig[]>([]);

  // Stable date references
  const startDate = useMemo(
    () => filterState?.startDate || moment().subtract(3, 'months').toDate(),
    [filterState?.startDate],
  );
  const endDate = useMemo(() => filterState?.endDate || new Date(), [filterState?.endDate]);

  // Format dates for API
  const formatDateForAPI = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Load WKZ options
  useEffect(() => {
    const loadWkzOptions = async () => {
      try {
        const wkzList = await fetchPlxWerbeaktionConfig();
        setWkzOptions(wkzList);
      } catch (error) {
        console.error('Error loading WKZ options:', error);
      }
    };
    loadWkzOptions();
  }, []);

  // Fetch data
  useEffect(() => {
    // Abbruchschutz (Critical Review 28.10.): eingebettet kommt der URL-Zeitraum erst nach dem Mount an — die spätere
    // Antwort des alten Zeitraums darf die neue nicht überschreiben.
    let abgebrochen = false;
    const loadData = async () => {
      setLoading(true);
      setFehler(null);
      try {
        const result = await fetchAnfoValuation(
          formatDateForAPI(startDate),
          formatDateForAPI(endDate),
          selectedWkz.length > 0 ? selectedWkz : undefined,
          excludeTestOrders,
        );
        if (abgebrochen) return;
        setData(result.rows);
        setSummary(result.summary);
      } catch (error) {
        console.error('Error loading Anfo valuation data:', error);
        if (!abgebrochen) setFehler(error instanceof Error ? error.message : String(error));
      } finally {
        if (!abgebrochen) setLoading(false);
      }
    };

    loadData();
    return () => {
      abgebrochen = true;
    };
  }, [startDate, endDate, selectedWkz, excludeTestOrders]);

  if (loading) {
    return <LoadingSpinner />;
  }
  if (fehler) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Die Anfo-Bewertung konnte nicht geladen werden ({fehler}). Bitte die Seite neu laden.
      </Alert>
    );
  }

  // Summen kommen aus dem Backend (dieselbe Bestellmenge wie die Tabelle), nicht mehr aus data.reduce.
  const totalAnfos = summary?.total_anfos ?? 0;
  const totalActualRevenue = Number(summary?.actual_revenue) || 0;
  const totalExpectedRevenue = Number(summary?.expected_total_revenue) || 0;

  // KPI Cards data
  const kpiCards = [
    {
      title: TERMS.anfos.label,
      tip: TERMS.anfos.tip,
      value: formatNumber(totalAnfos, 0),
      subtitle: 'Bestellungen (ohne Verweigert)',
      color: colors.blueAccent[600],
    },
    {
      title: TERMS.umsatz.label,
      tip: TERMS.umsatz.tip,
      value: `${formatNumber(totalActualRevenue, 2)} €`,
      subtitle: 'Tatsächlich bezahlt, Gutschriften abgezogen',
      color: colors.greenAccent[600],
    },
    {
      title: TERMS.anfoWert.label,
      tip: TERMS.anfoWert.tip,
      value: `${formatNumber(totalExpectedRevenue, 2)} €`,
      subtitle: 'Potenzial (Summe der Anfo-Werte je Anfo), kein Zahlungseingang',
      color: colors.primary[600],
    },
  ];

  // Header mapping for TableWidget
  const detailsHeaderMapping: Record<string, HeaderMappingItem> = {
    product_name: { name: 'Produkt', type: 'string' },
    total_anfos: { name: 'Anfos', type: 'number', digits: 0 },
    // „Mit Zahlung" = Zahlungseingang ueber 1 EUR. Die Anfo-Uebersicht fuehrt daneben „Bezahlte
    // Orders" nach Abo-Klasse — die weicht ab (Jan-Aug 2026: 512 gegen 505), weil die Klasse z. B.
    // ein in der Testphase beendetes Abo trotz Zahlung als Unbezahlt fuehrt. Zwei Zahlen, zwei Namen.
    paid_anfos: { name: 'Mit Zahlung', type: 'number', digits: 0 },
    conversion_rate: { name: 'Conv. %', type: 'number', digits: 1, attach: ' %' },
    active_count: { name: 'Aktiv', type: 'number', digits: 0 },
    ended_count: { name: 'Beendet', type: 'number', digits: 0 },
    cancelled_count: { name: 'Gekündigt', type: 'number', digits: 0 },
    trial_count: { name: 'Testphase', type: 'number', digits: 0 },
    unpaid_count: { name: 'Unbezahlt', type: 'number', digits: 0 },
    actual_revenue: { name: 'Umsatz (bezahlt)', type: 'number', digits: 2, attach: ' €' },
    revenue_per_anfo: { name: 'Umsatz/Anfo', type: 'number', digits: 2, attach: ' €' },
    // Summenzeile = Anfo-Wert-Summe ÷ ALLE Anfos (Produkte ohne Anfo-Wert zaehlen mit 0). Die
    // Anfo-Uebersicht mittelt nur ueber Anfos mit hinterlegtem Wert — daher der eigene Name.
    expected_anfo_value: { name: 'Anfo-Wert je Anfo', type: 'number', digits: 2, attach: ' €' },
    model_window: {
      name: 'Grundlage',
      type: 'custom',
      customRender: (v: unknown, _row?: unknown, isSummary?: boolean) => (
        <span>{isSummary ? '' : (WINDOW_LABEL[String(v)] ?? '–')}</span>
      ),
    },
    model_n: { name: 'n (Modell)', type: 'number', digits: 0 },
    model_r: { name: 'Modell: R (75 T.)', type: 'number', digits: 2, attach: ' €' },
    model_g: { name: 'G', type: 'number', digits: 3 },
    rev75_mean: { name: 'Ø bis Tag 75 (reif)', type: 'number', digits: 2, attach: ' €' },
    rev75_n: { name: 'reife Anfos', type: 'number', digits: 0 },
    divergence: { name: 'Abweichung zu R', type: 'number', digits: 2, attach: ' €' },
    expected_total_revenue: {
      name: TERMS.anfoWert.label,
      type: 'number',
      digits: 2,
      attach: ' €',
    },
  };

  // Summenzeile = Backend-Summary (gesamte gefilterte Menge), unabhaengig von Seite und Sortierung.
  const computeSummary = (): AnfoValuationRow =>
    summary ?? {
      product_name: 'Insgesamt',
      artikel: '',
      total_anfos: 0,
      paid_anfos: 0,
      conversion_rate: 0,
      active_count: 0,
      ended_count: 0,
      cancelled_count: 0,
      trial_count: 0,
      unpaid_count: 0,
      actual_revenue: 0,
      expected_anfo_value: 0,
      expected_total_revenue: 0,
      revenue_per_anfo: 0,
      divergence: null,
    };

  return (
    <Box m={eingebettet ? 0 : '20px'}>
      {!eingebettet && (
        <Header title="PLX Anfo-Bewertung" subtitle="Anfo-Wert je Produkt gegen den bezahlten Umsatz" />
      )}

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={excludeTestOrders}
              onChange={(e) => setExcludeTestOrders(e.target.checked)}
              sx={{ color: colors.greenAccent[200] }}
            />
          }
          label="Testbestellungen ausschließen"
        />

        <Autocomplete
          multiple
          options={wkzOptions}
          getOptionLabel={(option) => `${option.werbeaktion_wbkz} (${option.plx_product || 'N/A'})`}
          value={wkzOptions.filter((w) => selectedWkz.includes(w.werbeaktion_wbkz))}
          onChange={(_, newValue) => setSelectedWkz(newValue.map((v) => v.werbeaktion_wbkz))}
          renderInput={(params) => (
            <TextField {...params} label="WKZ Filter" placeholder="Kampagnen auswählen..." />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...chipProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option.werbeaktion_wbkz}
                  {...chipProps}
                  sx={{ backgroundColor: colors.blueAccent[700] }}
                />
              );
            })
          }
          sx={{ minWidth: 300 }}
        />
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpiCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" color={colors.grey[100]} gutterBottom>
                  {card.title}
                  <InfoTip text={card.tip} />
                </Typography>
                <Typography variant="h4" sx={{ color: card.color, fontWeight: 'bold', mb: 0.5 }}>
                  {card.value}
                </Typography>
                <Typography variant="caption" color={colors.grey[300]}>
                  {card.subtitle}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box mb={3}>
        <Explainer title="Anfo-Wert gegen die tatsächlichen Zahlungen" sections={EXPLAINER} />
      </Box>

      {/* Product Performance Table */}
      <Card elevation={2}>
        <CardContent>
          <Typography variant="h5" mb={2}>
            Produkt-Performance
          </Typography>
          <Box
            sx={{
              backgroundColor: colors.primary[400],
              borderRadius: '4px',
              overflow: 'hidden',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <TableWidget<AnfoValuationRow>
              getData={async () => data}
              computeSummary={computeSummary}
              detailsHeaderMapping={detailsHeaderMapping}
              initialSortColumn="expected_total_revenue"
              sortOrder="desc"
              excelDownloadName={`Anfo_Bewertung_${formatDateForAPI(startDate)}_bis_${formatDateForAPI(endDate)}`}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AnfoValuationPage;
